import { useEffect, useMemo, useState } from "react";
import { cn } from "@/lib/utils";
import type { PeriodPreset } from "@/data/types";

export type DateRange = { start: string; end: string };

interface DateRangePickerProps {
  preset: PeriodPreset;
  displayRange: DateRange;
  presetLabels: Record<PeriodPreset, string>;
  presetOrder: PeriodPreset[];
  today: Date;
  onPresetSelect: (preset: PeriodPreset) => void;
  onRangeApply: (range: DateRange) => void;
  onClose: () => void;
}

const WEEKDAYS = ["seg", "ter", "qua", "qui", "sex", "sáb", "dom"];
const MONTHS_PT = [
  "janeiro", "fevereiro", "março", "abril", "maio", "junho",
  "julho", "agosto", "setembro", "outubro", "novembro", "dezembro",
];

export function DateRangePicker({
  preset,
  displayRange,
  presetLabels,
  presetOrder,
  today,
  onPresetSelect,
  onRangeApply,
  onClose,
}: DateRangePickerProps) {
  // Range "em construção" — só é comitado no Aplicar.
  const [workingRange, setWorkingRange] = useState<DateRange>(displayRange);
  // Quando usuário clicou 1 dia e ainda não completou, guarda aqui.
  const [pendingAnchor, setPendingAnchor] = useState<string | null>(null);
  const [hoverDate, setHoverDate] = useState<string | null>(null);
  const [viewYear, setViewYear] = useState<number>(new Date(displayRange.end).getFullYear());
  const [viewMonth, setViewMonth] = useState<number>(new Date(displayRange.end).getMonth());

  // Sincroniza quando o preset muda externamente (reset).
  useEffect(() => {
    setWorkingRange(displayRange);
    setPendingAnchor(null);
    setHoverDate(null);
  }, [displayRange.start, displayRange.end]);

  const displayStart = workingRange.start;
  const displayEnd = workingRange.end;

  // Quando há pendingAnchor, o preview vai do anchor até o dia sobre o qual o mouse está.
  const previewRange = useMemo<DateRange | null>(() => {
    if (!pendingAnchor) return null;
    const other = hoverDate ?? pendingAnchor;
    return pendingAnchor <= other
      ? { start: pendingAnchor, end: other }
      : { start: other, end: pendingAnchor };
  }, [pendingAnchor, hoverDate]);

  const effectiveRange = previewRange ?? workingRange;

  const handleDayClick = (iso: string) => {
    if (!pendingAnchor) {
      setPendingAnchor(iso);
      return;
    }
    const range: DateRange = pendingAnchor <= iso
      ? { start: pendingAnchor, end: iso }
      : { start: iso, end: pendingAnchor };
    setWorkingRange(range);
    setPendingAnchor(null);
    setHoverDate(null);
  };

  const changeMonth = (delta: number) => {
    let m = viewMonth + delta;
    let y = viewYear;
    while (m < 0) { m += 12; y -= 1; }
    while (m > 11) { m -= 12; y += 1; }
    setViewMonth(m);
    setViewYear(y);
  };

  const handleApply = () => {
    onRangeApply(workingRange);
    onClose();
  };

  const handleInputChange = (key: "start" | "end", value: string) => {
    if (!value) return;
    const next = { ...workingRange, [key]: value };
    if (next.start > next.end) {
      // Se inverteu, mantém integridade trocando.
      setWorkingRange(key === "start" ? { start: value, end: value } : { start: next.start, end: value });
    } else {
      setWorkingRange(next);
    }
    setPendingAnchor(null);
  };

  return (
    <div className="flex w-[640px] overflow-hidden rounded-xl border border-border-strong bg-bg-card shadow-2xl">
      {/* Sidebar de presets */}
      <aside className="w-[180px] shrink-0 border-r border-border-subtle bg-bg-secondary/60 p-2">
        <div className="px-2 pb-1.5 pt-1 text-[10px] font-medium uppercase tracking-[0.12em] text-text-muted">
          Períodos
        </div>
        <nav className="space-y-0.5">
          {presetOrder.filter((p) => p !== "custom").map((p) => (
            <button
              key={p}
              type="button"
              onClick={() => onPresetSelect(p)}
              className={cn(
                "flex w-full items-center justify-between rounded-md px-3 py-1.5 text-left text-[13px] transition",
                preset === p
                  ? "bg-brand-amber/10 font-semibold text-brand-amber"
                  : "text-text-secondary hover:bg-bg-card-hover hover:text-text-primary"
              )}
            >
              <span>{presetLabels[p]}</span>
              {preset === p && <span className="text-[10px]">●</span>}
            </button>
          ))}
        </nav>

        <div className="mt-3 border-t border-border-subtle pt-2">
          <button
            type="button"
            onClick={() => onPresetSelect("custom")}
            className={cn(
              "flex w-full items-center justify-between rounded-md px-3 py-1.5 text-left text-[13px] transition",
              preset === "custom"
                ? "bg-brand-amber/10 font-semibold text-brand-amber"
                : "text-text-secondary hover:bg-bg-card-hover hover:text-text-primary"
            )}
          >
            <span>{presetLabels.custom}</span>
            {preset === "custom" && <span className="text-[10px]">●</span>}
          </button>
        </div>
      </aside>

      {/* Painel principal: inputs + calendário + ações */}
      <div className="flex flex-1 flex-col">
        {/* Inputs de data */}
        <div className="flex items-center gap-2 border-b border-border-subtle px-5 py-4">
          <DateInputBox
            label="De"
            value={displayStart}
            min=""
            max={displayEnd}
            onChange={(v) => handleInputChange("start", v)}
            highlighted
          />
          <span className="text-text-muted">→</span>
          <DateInputBox
            label="Até"
            value={displayEnd}
            min={displayStart}
            max=""
            onChange={(v) => handleInputChange("end", v)}
          />
        </div>

        {/* Navegação mês/ano */}
        <div className="flex items-center justify-between px-5 pt-4">
          <button
            type="button"
            onClick={() => changeMonth(-1)}
            className="flex h-7 w-7 items-center justify-center rounded-md text-text-secondary transition hover:bg-bg-card-hover hover:text-text-primary"
            aria-label="Mês anterior"
          >
            ‹
          </button>
          <div className="flex items-center gap-2">
            <span className="text-sm font-semibold capitalize text-text-primary">{MONTHS_PT[viewMonth]}</span>
            <span className="text-sm text-text-muted">{viewYear}</span>
          </div>
          <button
            type="button"
            onClick={() => changeMonth(1)}
            className="flex h-7 w-7 items-center justify-center rounded-md text-text-secondary transition hover:bg-bg-card-hover hover:text-text-primary"
            aria-label="Próximo mês"
          >
            ›
          </button>
        </div>

        {/* Calendário */}
        <Calendar
          year={viewYear}
          month={viewMonth}
          today={today}
          range={effectiveRange}
          onDayClick={handleDayClick}
          onDayHover={setHoverDate}
        />

        {/* Ações */}
        <footer className="flex items-center justify-between border-t border-border-subtle px-5 py-3">
          <span className="text-xs text-text-muted">
            {pendingAnchor
              ? "Selecione o dia final…"
              : `${formatShort(workingRange.start)} → ${formatShort(workingRange.end)}`}
          </span>
          <div className="flex gap-2">
            <button
              type="button"
              onClick={onClose}
              className="rounded-md border border-border-subtle px-3 py-1.5 text-xs font-medium text-text-secondary transition hover:border-border-strong hover:text-text-primary"
            >
              Cancelar
            </button>
            <button
              type="button"
              onClick={handleApply}
              className="rounded-md bg-brand-amber px-4 py-1.5 text-xs font-semibold text-text-on-accent transition hover:brightness-110"
            >
              Aplicar
            </button>
          </div>
        </footer>
      </div>
    </div>
  );
}

function DateInputBox({
  label,
  value,
  min,
  max,
  onChange,
  highlighted,
}: {
  label: string;
  value: string;
  min: string;
  max: string;
  onChange: (v: string) => void;
  highlighted?: boolean;
}) {
  return (
    <label
      className={cn(
        "flex flex-1 flex-col rounded-md border bg-bg-primary px-3 py-1.5 transition focus-within:border-brand-amber",
        highlighted ? "border-brand-amber" : "border-border-subtle"
      )}
    >
      <span className="text-[10px] font-medium uppercase tracking-[0.12em] text-text-muted">{label}</span>
      <input
        type="date"
        value={value}
        min={min || undefined}
        max={max || undefined}
        onChange={(e) => onChange(e.target.value)}
        className="bg-transparent text-sm text-text-primary focus:outline-none"
      />
    </label>
  );
}

function Calendar({
  year,
  month,
  today,
  range,
  onDayClick,
  onDayHover,
}: {
  year: number;
  month: number;
  today: Date;
  range: DateRange;
  onDayClick: (iso: string) => void;
  onDayHover: (iso: string | null) => void;
}) {
  const cells = useMemo(() => buildMonthCells(year, month), [year, month]);
  const todayIso = toIso(today);

  return (
    <div className="px-4 pb-2 pt-3" onMouseLeave={() => onDayHover(null)}>
      <div className="mb-1.5 grid grid-cols-7 px-1 text-center text-[10px] font-medium uppercase tracking-wider text-text-muted">
        {WEEKDAYS.map((d) => (
          <span key={d}>{d}</span>
        ))}
      </div>
      <div className="grid grid-cols-7 gap-y-0.5">
        {cells.map((cell) => {
          const iso = cell.iso;
          const inCurrentMonth = cell.month === month;
          const inRange = iso >= range.start && iso <= range.end;
          const isStart = iso === range.start;
          const isEnd = iso === range.end;
          const isToday = iso === todayIso;

          return (
            <button
              key={iso}
              type="button"
              onClick={() => onDayClick(iso)}
              onMouseEnter={() => onDayHover(iso)}
              className={cn(
                "relative flex h-9 items-center justify-center text-sm transition",
                !inCurrentMonth && "text-text-muted/50",
                inCurrentMonth && !inRange && "text-text-primary hover:bg-bg-card-hover",
                inRange && !isStart && !isEnd && "bg-brand-amber/20 text-text-primary",
                (isStart || isEnd) && "bg-brand-amber font-semibold text-text-on-accent",
                isStart && !isEnd && "rounded-l-md",
                isEnd && !isStart && "rounded-r-md",
                isStart && isEnd && "rounded-md"
              )}
            >
              {cell.day}
              {isToday && !isStart && !isEnd && (
                <span className="absolute bottom-1 h-0.5 w-4 rounded-full bg-brand-amber" />
              )}
            </button>
          );
        })}
      </div>
    </div>
  );
}

// Monta 42 células (6 semanas) alinhando semana iniciando em seg (0).
function buildMonthCells(year: number, month: number) {
  const first = new Date(year, month, 1);
  // Em JS getDay: 0=dom, 1=seg, ..., 6=sáb. Queremos seg=0.
  const weekdayOfFirst = (first.getDay() + 6) % 7;
  const daysInMonth = new Date(year, month + 1, 0).getDate();

  const cells: { day: number; month: number; year: number; iso: string }[] = [];
  // Prev month fill
  const prev = new Date(year, month, 0);
  const prevDays = prev.getDate();
  for (let i = weekdayOfFirst - 1; i >= 0; i--) {
    const d = prevDays - i;
    const pm = month === 0 ? 11 : month - 1;
    const py = month === 0 ? year - 1 : year;
    cells.push({ day: d, month: pm, year: py, iso: toIsoParts(py, pm, d) });
  }
  // Current month
  for (let d = 1; d <= daysInMonth; d++) {
    cells.push({ day: d, month, year, iso: toIsoParts(year, month, d) });
  }
  // Next month fill até 42
  let nd = 1;
  while (cells.length < 42) {
    const nm = month === 11 ? 0 : month + 1;
    const ny = month === 11 ? year + 1 : year;
    cells.push({ day: nd, month: nm, year: ny, iso: toIsoParts(ny, nm, nd) });
    nd++;
  }
  return cells;
}

function toIso(d: Date): string {
  return toIsoParts(d.getFullYear(), d.getMonth(), d.getDate());
}

function toIsoParts(y: number, m: number, d: number): string {
  return `${y}-${String(m + 1).padStart(2, "0")}-${String(d).padStart(2, "0")}`;
}

function formatShort(iso: string): string {
  const d = new Date(iso);
  return d.toLocaleDateString("pt-BR", { day: "2-digit", month: "short", year: "numeric" });
}
